import React, { useCallback, useState } from "react";
import { Form, Input, Select, DatePicker, Button, Typography, Space, Modal, message } from "antd";
import { useAuth } from "../../hooks/useAuth";
import { useIntl } from "react-intl";
import { useCreateApiKeyMutation } from "../../services/nestjs-base-api";
import { ICreateApiKeyRequest } from "../../services/nestjs-base-api/types";
import { CopyOutlined, CheckOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { Option } = Select;

interface IProps {
    onClose: () => void;
}

const ApiKeyCreate: React.FunctionComponent<IProps> = ({ onClose }) => {
    const { user } = useAuth();
    const intl = useIntl();
    const [form] = Form.useForm();
    const [createApiKey, { isLoading }] = useCreateApiKeyMutation();
    const [createdApiKey, setCreatedApiKey] = useState<string | null>(null);
    const [apiKeyCopied, setApiKeyCopied] = useState(false);

    const scopeOptions = [
        { value: 'external_metrics', label: intl.formatMessage({ id: 'apiKeys.scope.external_metrics' }) },
        { value: 'surveys', label: intl.formatMessage({ id: 'apiKeys.scope.surveys' }) },
        { value: 'customers', label: intl.formatMessage({ id: 'apiKeys.scope.customers', defaultMessage: 'Customers (segmentation + integrations)' }) },
        { value: 'full_access', label: intl.formatMessage({ id: 'apiKeys.scope.full_access' }) },
    ];

    const onFinish = useCallback(async (values: any) => {
        try {
            const payload: ICreateApiKeyRequest = {
                name: values.name,
                scopes: values.scopes,
                expiresAt: values.expiresAt ? values.expiresAt.toISOString() : undefined,
            };

            const result = await createApiKey({
                jwtToken: user.jwtToken,
                organisationId: user.organisation.id,
                payload,
            }).unwrap();

            setCreatedApiKey(result.data.apiKey);
            message.success(result.message);
        } catch (error: any) {
            message.error(error?.data?.message || 'Failed to create API key');
        }
    }, [user, createApiKey]);

    const copyToClipboard = useCallback(async () => {
        if (createdApiKey) {
            try {
                await navigator.clipboard.writeText(createdApiKey);
                setApiKeyCopied(true);
                message.success(intl.formatMessage({ id: 'apiKeys.created.copiedMessage' }));
                setTimeout(() => setApiKeyCopied(false), 2000);
            } catch (error) {
                // Fallback for older browsers
                const textArea = document.createElement('textarea');
                textArea.value = createdApiKey;
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand('copy');
                document.body.removeChild(textArea);
                message.success(intl.formatMessage({ id: 'apiKeys.created.copiedMessage' }));
            }
        }
    }, [createdApiKey, intl]);

    const handleClose = useCallback(() => {
        if (createdApiKey) {
            Modal.confirm({
                title: 'Important',
                content: 'Have you copied your API key? You won\'t be able to see it again after closing this dialog.',
                okText: 'Yes, I have copied it',
                cancelText: 'Not yet',
                onOk: onClose,
            });
        } else {
            onClose();
        }
    }, [createdApiKey, onClose]);

    if (createdApiKey) {
        return (
            <div style={{ padding: '24px' }}>
                <Title level={4} style={{ color: '#52c41a', marginBottom: '16px' }}>
                    {intl.formatMessage({ id: 'apiKeys.created.title' })}
                </Title>
                
                <Text type="warning" style={{ 
                    display: 'block', 
                    marginBottom: '24px',
                    padding: '12px',
                    backgroundColor: '#fff7e6',
                    border: '1px solid #ffd666',
                    borderRadius: '6px'
                }}>
                    {intl.formatMessage({ id: 'apiKeys.created.warning' })}
                </Text>

                <div style={{ marginBottom: '24px' }}>
                    <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                        {intl.formatMessage({ id: 'apiKeys.created.keyLabel' })}
                    </Text>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <Input
                            value={createdApiKey}
                            readOnly
                            style={{ flex: 1 }}
                        />
                        <Button
                            type="primary"
                            icon={apiKeyCopied ? <CheckOutlined /> : <CopyOutlined />}
                            onClick={copyToClipboard}
                            style={{ flexShrink: 0 }}
                        >
                            {apiKeyCopied ? 'Copied!' : intl.formatMessage({ id: 'apiKeys.created.copyButton' })}
                        </Button>
                    </div>
                </div>

                <div style={{ textAlign: 'right' }}>
                    <Button type="primary" onClick={handleClose}>
                        {intl.formatMessage({ id: 'common.okButton' })}
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div style={{ padding: '24px' }}>
            <Title level={4} style={{ marginBottom: '16px' }}>
                {intl.formatMessage({ id: 'apiKeys.create.title' })}
            </Title>

            <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                initialValues={{
                    scopes: ['external_metrics'],
                }}
            >
                <Form.Item
                    name="name"
                    label={intl.formatMessage({ id: 'apiKeys.create.nameLabel' })}
                    rules={[
                        {
                            required: true,
                            message: intl.formatMessage({ id: 'apiKeys.create.nameRequired' }),
                        },
                    ]}
                >
                    <Input
                        placeholder={intl.formatMessage({ id: 'apiKeys.create.namePlaceholder' })}
                    />
                </Form.Item>

                <Form.Item
                    name="scopes"
                    label={intl.formatMessage({ id: 'apiKeys.create.scopesLabel' })}
                    rules={[
                        {
                            required: true,
                            message: intl.formatMessage({ id: 'apiKeys.create.scopesRequired' }),
                        },
                    ]}
                >
                    <Select
                        mode="multiple"
                        placeholder={intl.formatMessage({ id: 'apiKeys.create.scopesPlaceholder' })}
                    >
                        {scopeOptions.map((option) => (
                            <Option key={option.value} value={option.value}>
                                {option.label}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    name="expiresAt"
                    label={intl.formatMessage({ id: 'apiKeys.create.expiresLabel' })}
                >
                    <DatePicker
                        showTime
                        style={{ width: '100%' }}
                        placeholder={intl.formatMessage({ id: 'apiKeys.create.expiresPlaceholder' })}
                        disabledDate={(current) => current && current < dayjs().endOf('day')}
                    />
                </Form.Item>

                <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                    <Space>
                        <Button onClick={handleClose}>
                            {intl.formatMessage({ id: 'common.cancelButton' })}
                        </Button>
                        <Button type="primary" htmlType="submit" loading={isLoading}>
                            {intl.formatMessage({ id: 'apiKeys.create.createButton' })}
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </div>
    );
};

export default ApiKeyCreate;